@if (!(unifiedPopupLayout && sheetFixedChrome) && !sttSupported) {
  <div class="alert alert-warning mb-3" role="alert">
    {{ 'voiceAssistant.browserUnsupported' | translate }}
  </div>
}

@if (showTitle) {
  <h2 class="h5 mb-3">{{ 'voiceAssistant.title' | translate }}</h2>
}

<ng-template #eduToolsSection>
  <section
    class="va-section va-edu-dropdown-section"
    role="region"
    [attr.aria-label]="'voiceAssistant.capabilitiesHeading' | translate">
    <h3 class="va-section__title">
      {{ 'voiceAssistant.capabilitiesHeading' | translate }}
    </h3>
    <div class="va-edu-tools">
      <button
        type="button"
        class="btn w-100 text-start d-flex align-items-center justify-content-between gap-2 va-edu-trigger"
        aria-haspopup="listbox"
        [attr.aria-expanded]="eduToolsExpanded()"
        (click)="toggleEducationalTools()">
        <span class="text-truncate flex-grow-1">
          @if (selectedEducationalTool(); as tool) {
            @if (tool === 'general_chat') {
              {{ 'voiceAssistant.generalChatOptionTitle' | translate }}
            } @else {
              {{ 'virtualAssistant.artifacts.' + tool | translate }}
            }
          } @else {
            {{ 'voiceAssistant.selectEducationalTool' | translate }}
          }
        </span>
        <span
          class="va-edu-trigger__chevron"
          [class.va-edu-trigger__chevron--open]="eduToolsExpanded()"
          aria-hidden="true"></span>
      </button>
      @if (eduToolsExpanded()) {
        <div
          class="va-edu-grid va-edu-grid--inline"
          role="listbox"
          [attr.aria-label]="'voiceAssistant.capabilitiesHeading' | translate">
          @for (artifact of artifactKeys; track artifact) {
            <button
              type="button"
              class="va-edu-tile"
              role="option"
              (click)="selectEducationalTool(artifact)">
              <span class="va-edu-tile__icon-wrap">
                <img
                  [src]="artifactIconSrc(artifact)"
                  [alt]="''"
                  class="va-edu-tile__img" />
              </span>
              <span class="va-edu-tile__title">{{
                'virtualAssistant.artifacts.' + artifact | translate
              }}</span>
              <span class="va-edu-tile__desc">{{
                'virtualAssistant.descriptions.' + artifact | translate
              }}</span>
            </button>
          }
          <button
            type="button"
            class="va-edu-tile va-edu-tile--chat"
            role="option"
            (click)="selectGeneralChat()">
            <span
              class="va-edu-tile__icon-wrap va-edu-tile__icon-wrap--chat"
              aria-hidden="true"></span>
            <span class="va-edu-tile__title">{{
              'voiceAssistant.generalChatOptionTitle' | translate
            }}</span>
            <span class="va-edu-tile__desc">{{
              'voiceAssistant.generalChatOptionHint' | translate
            }}</span>
          </button>
        </div>
      }
    </div>
  </section>
</ng-template>

<ng-template #workflowAssistantToolbar>
  <div class="va-workflow-toolbar-hero">
    <div
      class="va-workflow-inline-actions va-workflow-inline-actions--hero d-flex flex-wrap align-items-center justify-content-between gap-2 px-1">
      <span class="small text-muted">{{
        'voiceAssistant.assistantLabel' | translate
      }}</span>
      <div class="va-response-card__actions d-flex flex-shrink-0 gap-1">
        <button
          type="button"
          class="va-icon-btn"
          (click)="orchestrator.replayLastReply()"
          [attr.title]="'voiceAssistant.replay' | translate"
          [attr.aria-label]="'voiceAssistant.replay' | translate">
          <span
            class="va-icon-btn__glyph va-icon-btn__glyph--replay"
            aria-hidden="true"></span>
        </button>
        <button
          type="button"
          class="va-icon-btn"
          (click)="stopAll()"
          [attr.title]="'voiceAssistant.stop' | translate"
          [attr.aria-label]="'voiceAssistant.stop' | translate">
          <span
            class="va-icon-btn__glyph va-icon-btn__glyph--stop"
            aria-hidden="true"></span>
        </button>
        <button
          type="button"
          class="va-icon-btn"
          (click)="copyLastReply()"
          [attr.title]="'voiceAssistant.copy' | translate"
          [attr.aria-label]="'voiceAssistant.copy' | translate">
          <span
            class="va-icon-btn__glyph va-icon-btn__glyph--copy"
            aria-hidden="true"></span>
        </button>
      </div>
    </div>
    @if (copySucceeded()) {
      <p class="va-response-card__copied small mb-0 px-1 mt-1">
        {{ 'voiceAssistant.copied' | translate }}
      </p>
    }
  </div>
</ng-template>

<ng-template #chatThread>
  <div class="va-chat-thread" role="log" aria-live="polite">
    @for (msg of conversationState.chatMessages(); track msg.id) {
      @if (msg.role === 'user') {
        <div class="va-chat-row va-chat-row--user">
          <div class="va-chat-bubble va-chat-bubble--user">
            <p class="va-chat-bubble__text mb-0">{{ msg.text }}</p>
          </div>
        </div>
      } @else {
        <div class="va-chat-row va-chat-row--assistant">
          <div class="va-chat-bubble va-chat-bubble--assistant">
            @if (isLatestAssistantChatMessage(msg)) {
              <div class="va-chat-bubble__head d-flex align-items-center justify-content-between gap-2 mb-2">
                <span class="va-chat-bubble__label small text-muted mb-0">{{
                  'voiceAssistant.assistantLabel' | translate
                }}</span>
                @if (assistantChatMessageHasActions(msg)) {
                  <div class="va-response-card__actions d-flex flex-shrink-0 gap-1">
                    <button
                      type="button"
                      class="va-icon-btn"
                      (click)="orchestrator.replayLastReply()"
                      [attr.title]="'voiceAssistant.replay' | translate"
                      [attr.aria-label]="'voiceAssistant.replay' | translate">
                      <span
                        class="va-icon-btn__glyph va-icon-btn__glyph--replay"
                        aria-hidden="true"></span>
                    </button>
                    <button
                      type="button"
                      class="va-icon-btn"
                      (click)="stopAll()"
                      [attr.title]="'voiceAssistant.stop' | translate"
                      [attr.aria-label]="'voiceAssistant.stop' | translate">
                      <span
                        class="va-icon-btn__glyph va-icon-btn__glyph--stop"
                        aria-hidden="true"></span>
                    </button>
                    <button
                      type="button"
                      class="va-icon-btn"
                      (click)="copyLastReply()"
                      [attr.title]="'voiceAssistant.copy' | translate"
                      [attr.aria-label]="'voiceAssistant.copy' | translate">
                      <span
                        class="va-icon-btn__glyph va-icon-btn__glyph--copy"
                        aria-hidden="true"></span>
                    </button>
                  </div>
                }
              </div>
              @if (copySucceeded()) {
                <p class="va-response-card__copied small mb-2">
                  {{ 'voiceAssistant.copied' | translate }}
                </p>
              }
            }
            @switch (msg.kind) {
              @case ('text') {
                <p class="va-chat-bubble__text mb-0 va-workflow-prompt--preline">{{ msg.text }}</p>
              }
              @case ('quick') {
                <h4 class="h6 mb-2">{{ msg.quick.title }}</h4>
                <p class="mb-0 small va-chat-bubble__text va-workflow-prompt--preline">
                  {{ msg.quick.content }}
                </p>
                @if (msg.quick.notes) {
                  <p class="mb-0 mt-2 small text-muted">{{ msg.quick.notes }}</p>
                }
              }
              @case ('educational') {
                @if (msg.text) {
                  <p class="va-chat-bubble__text mb-2">{{ msg.text }}</p>
                }
                <app-educational-content-view
                  class="va-response-card__structured"
                  [view]="msg.view"
                  [embedMode]="true"
                  [artifactKey]="chatEducationalArtifactKey(msg)" />
              }
            }
          </div>
        </div>
      }
    }
    @if (orchestrator.liveTranscript(); as live) {
      @if (live.length > 0 && !orchestrator.isBusy()) {
        <div class="va-chat-row va-chat-row--user">
          <div class="va-chat-bubble va-chat-bubble--user va-chat-bubble--pending">
            <p class="va-chat-bubble__text mb-0">{{ live }}</p>
          </div>
        </div>
      }
    }
    <div class="va-chat-scroll-anchor" aria-hidden="true"></div>
  </div>
</ng-template>

@if (unifiedPopupLayout && sheetFixedChrome) {
  <div
    class="voice-panel voice-panel--sheet d-flex flex-column min-h-0 h-100 w-100 overflow-hidden">
    @if (!sttSupported) {
      <div
        class="alert alert-warning mb-2 py-2 small flex-shrink-0 va-alert-soft"
        role="alert">
        {{ 'voiceAssistant.browserUnsupported' | translate }}
      </div>
    }

    <!-- Voice-first hero -->
    <div
      class="voice-panel__fixed-top flex-shrink-0 va-voice-hero"
      [class.va-voice-hero--teacherPriority]="isTeacherCapturing()">
      <div
        class="va-voice-hero__status"
        [class.va-voice-hero__status--listening]="isTeacherCapturing()"
        [class.va-voice-hero__status--thinking]="
          orchestrator.assistantState() === 'thinking'
        "
        [class.va-voice-hero__status--speaking]="showAssistantSpeaking()"
        [class.va-voice-hero__status--error]="
          orchestrator.assistantState() === 'error'
        ">
        <span class="va-voice-hero__dot" aria-hidden="true"></span>
        <span class="va-voice-hero__status-text">
          @if (orchestrator.assistantState() === 'thinking') {
            {{ 'voiceAssistant.stateThinking' | translate }}
          } @else if (isTeacherCapturing()) {
            {{ 'voiceAssistant.stateListening' | translate }}
          } @else if (showAssistantSpeaking()) {
            {{ 'voiceAssistant.stateSpeaking' | translate }}
          } @else if (orchestrator.assistantState() === 'error') {
            {{ 'voiceAssistant.stateError' | translate }}
          } @else {
            {{ 'voiceAssistant.tapToSpeak' | translate }}
          }
        </span>
      </div>

      <div
        class="va-waveform"
        [class.va-waveform--active]="showWaveform()"
        aria-hidden="true">
        @for (i of waveformBarIndexes; track i) {
          <span
            class="va-waveform__bar"
            [style.animation-delay.ms]="i * 45"></span>
        }
      </div>

      <div class="va-voice-hero__mic-wrap">
        <button
          type="button"
          class="mic-btn rounded-circle d-flex align-items-center justify-content-center"
          [class.listening]="isTeacherCapturing()"
          [class.busy]="
            orchestrator.assistantState() === 'thinking' && !isTeacherCapturing()
          "
          [class.mic-btn--idle]="
            !isTeacherCapturing() &&
            orchestrator.assistantState() === 'idle' &&
            !showAssistantSpeaking()
          "
          [disabled]="!sttSupported"
          (click)="onMicClick()"
          [attr.aria-pressed]="isTeacherCapturing()"
          [attr.aria-label]="'voiceAssistant.micAria' | translate">
          <img
            [src]="voiceAssistantMarkSrc"
            alt=""
            class="mic-mark-img"
            width="30"
            height="30"
            decoding="async"
            draggable="false" />
        </button>
      </div>

      <p class="va-voice-hero__hint text-center mb-0">
        @if (orchestrator.assistantState() === 'thinking') {
          {{ 'voiceAssistant.thinkingHint' | translate }}
        } @else if (isTeacherCapturing()) {
          {{ 'voiceAssistant.listenHint' | translate }}
        } @else if (showAssistantSpeaking()) {
          {{ 'voiceAssistant.speakingHint' | translate }}
        } @else {
          {{ 'voiceAssistant.idleMicHint' | translate }}
        }
      </p>
    </div>

    <div
      #scrollHost
      class="voice-panel__scroll scrollbar-hidden flex-grow-1 min-h-0 d-flex flex-column gap-3 py-1 va-scroll-fade"
      [class.voice-panel__scroll--conversation]="!showDiscoveryChrome()">
      @if (showDiscoveryChrome()) {
        <div
          class="va-popup-greeting"
          role="region"
          aria-labelledby="va-popup-greet-title">
          <p
            id="va-popup-greet-title"
            class="va-popup-greeting__lead mb-1 fw-semibold">
            {{ 'voiceAssistant.popupGreeting' | translate }}
          </p>
          <p class="va-popup-greeting__intro small text-muted mb-0">
            {{ 'voiceAssistant.popupIntro' | translate }}
          </p>
        </div>
      }

      <ng-container *ngTemplateOutlet="chatThread" />

      @if (orchestrator.lastError()) {
        <div class="va-error-banner" role="alert">
          <span>{{ orchestrator.lastError() }}</span>
          <button
            type="button"
            class="btn btn-sm btn-outline-dark"
            (click)="retry()">
            {{ 'voiceAssistant.retry' | translate }}
          </button>
        </div>
      }

      @if (showWorkflowLayout() && conversationState.suggestionChips().length) {
        <div class="va-workflow-chips va-card-soft p-2 rounded-3" role="region">
          <span class="va-section__title va-section__title--sm d-block mb-2">{{
            'voiceAssistant.workflow.quickPick' | translate
          }}</span>
          <div class="d-flex flex-wrap gap-2">
            @for (c of conversationState.suggestionChips(); track c.slotKey + ':' + c.value) {
              <button type="button" class="btn btn-sm va-chip" (click)="onWorkflowChip(c)">
                {{ c.label }}
              </button>
            }
          </div>
        </div>
      }

      <!-- <section class="va-section" aria-labelledby="va-quick-actions-title">
        <h3 id="va-quick-actions-title" class="va-section__title">
          {{ 'voiceAssistant.quickActionsTitle' | translate }}
        </h3>
        <div class="va-quick-grid">
          @for (tile of quickActionTiles; track tile.commandId) {
            <button
              type="button"
              [ngClass]="['va-quick-tile', 'va-quick-tile--' + tile.icon]"
              (click)="onQuickAction(tile.commandId)">
              <span class="va-quick-tile__icon" aria-hidden="true"></span>
              <span class="va-quick-tile__label">{{ tile.labelKey | translate }}</span>
            </button>
          }
        </div>
      </section> -->

      @if (showDiscoveryChrome()) {
        <div class="quick-chips va-chips-wrap">
          <span class="va-section__title va-section__title--sm d-block mb-2">{{
            'voiceAssistant.quickCommands' | translate
          }}</span>
          <div class="d-flex flex-wrap gap-2">
            @for (chip of quickChips; track chip.id) {
              <button
                type="button"
                class="btn btn-sm va-chip"
                (click)="onQuickAction(chip.id)">
                {{ chip.labelKey | translate }}
              </button>
            }
          </div>
        </div>

        @if (!hybridEducationalLayout) {
          <ng-container *ngTemplateOutlet="eduToolsSection" />
        }
      }
    </div>

    @if (showDiscoveryChrome()) {
      <div
        class="voice-panel__fixed-bottom flex-shrink-0 pt-2 pb-2 voice-panel__footer-edge va-footer-stack">
        <p class="va-trust-footer mb-2">
          <span class="va-trust-footer__lock" aria-hidden="true"></span>
          {{ 'voiceAssistant.privacyFooter' | translate }}
        </p>
        <div class="va-manual-pill">
          <label class="visually-hidden" for="va-manual-input">{{
            'voiceAssistant.typeInsteadLabel' | translate
          }}</label>
          <div class="va-manual-pill__row">
            <input
              #manualInput
              id="va-manual-input"
              type="text"
              class="va-manual-pill__input"
              [(ngModel)]="manualText"
              (keyup.enter)="submitAssistantInput('enter')"
              [placeholder]="'voiceAssistant.manualPlaceholder' | translate" />
            <button
              type="button"
              class="va-manual-pill__send"
              (click)="onSendManual()"
              [disabled]="!hasAssistantInput()"
              [attr.aria-label]="'voiceAssistant.send' | translate">
              <span class="va-manual-pill__send-icon" aria-hidden="true"></span>
            </button>
          </div>
        </div>
      </div>
    }
  </div>
} @else {
  <div class="voice-panel scrollbar-hidden d-flex flex-column gap-3">
    @if (unifiedPopupLayout && showDiscoveryChrome()) {
      <div
        class="va-popup-greeting"
        role="region"
        aria-labelledby="va-popup-greet-title-legacy">
        <p
          id="va-popup-greet-title-legacy"
          class="va-popup-greeting__lead mb-1 fw-semibold">
          {{ 'voiceAssistant.popupGreeting' | translate }}
        </p>
        <p class="va-popup-greeting__intro small text-muted mb-0">
          {{ 'voiceAssistant.popupIntro' | translate }}
        </p>
      </div>
    }

    <div class="va-voice-hero va-voice-hero--compact">
      <div
        class="va-waveform va-waveform--compact mx-auto"
        [class.va-waveform--active]="showWaveform()"
        aria-hidden="true">
        @for (i of waveformBarIndexes; track i) {
          <span
            class="va-waveform__bar"
            [style.animation-delay.ms]="i * 45"></span>
        }
      </div>
      <div class="mic-row d-flex flex-column align-items-center gap-2">
        <button
          type="button"
          class="btn mic-btn rounded-circle d-flex align-items-center justify-content-center"
          [class.listening]="isTeacherCapturing()"
          [class.busy]="
            orchestrator.assistantState() === 'thinking' && !isTeacherCapturing()
          "
          [class.mic-btn--idle]="
            !isTeacherCapturing() &&
            orchestrator.assistantState() === 'idle' &&
            !showAssistantSpeaking()
          "
          [disabled]="!sttSupported"
          (click)="onMicClick()"
          [attr.aria-pressed]="isTeacherCapturing()"
          [attr.aria-label]="'voiceAssistant.micAria' | translate">
          <img
            [src]="voiceAssistantMarkSrc"
            alt=""
            class="mic-mark-img"
            width="32"
            height="32"
            decoding="async"
            draggable="false" />
        </button>
        <span class="state-label small text-muted text-center">
          @if (orchestrator.assistantState() === 'thinking') {
            {{ 'voiceAssistant.stateThinking' | translate }}
          } @else if (isTeacherCapturing()) {
            {{ 'voiceAssistant.stateListening' | translate }}
          } @else if (showAssistantSpeaking()) {
            {{ 'voiceAssistant.stateSpeaking' | translate }}
          } @else if (orchestrator.assistantState() === 'error') {
            {{ 'voiceAssistant.stateError' | translate }}
          } @else {
            {{ 'voiceAssistant.tapToSpeak' | translate }}
          }
        </span>
      </div>
    </div>

    <ng-container *ngTemplateOutlet="chatThread" />

    @if (orchestrator.lastError()) {
      <div class="va-error-banner" role="alert">
        <span>{{ orchestrator.lastError() }}</span>
        <button
          type="button"
          class="btn btn-sm btn-outline-dark"
          (click)="retry()">
          {{ 'voiceAssistant.retry' | translate }}
        </button>
      </div>
    }

    @if (showDiscoveryChrome()) {
      @if (unifiedPopupLayout && !hybridEducationalLayout) {
        <ng-container *ngTemplateOutlet="eduToolsSection" />
      }

      <div class="quick-chips">
        <span class="small text-muted d-block mb-1">{{
          'voiceAssistant.quickCommands' | translate
        }}</span>
        <div class="d-flex flex-wrap gap-2">
          @for (chip of quickChips; track chip.id) {
            <button
              type="button"
              class="btn btn-sm va-chip"
              (click)="onQuickAction(chip.id)">
              {{ chip.labelKey | translate }}
            </button>
          }
        </div>
      </div>

      <div class="manual-fallback">
        <label class="form-label small" for="va-manual-input-legacy">{{
          'voiceAssistant.manualLabel' | translate
        }}</label>
        <div class="input-group">
          <input
            #manualInput
            id="va-manual-input-legacy"
            type="text"
            class="form-control"
            [(ngModel)]="manualText"
            (keyup.enter)="submitAssistantInput('enter')"
            [placeholder]="'voiceAssistant.manualPlaceholder' | translate" />
          <button
            type="button"
            class="btn btn-primary"
            (click)="onSendManual()"
            [disabled]="!hasAssistantInput()">
            {{ 'voiceAssistant.send' | translate }}
          </button>
        </div>
      </div>
    }
  </div>
}
